import { useCallback, useReducer } from 'react'
import moment from 'moment'
import { useIntl } from 'react-intl'
import effects from 'app/effects/effects'
import { useDispatch } from 'react-redux'
import { isNil, mergeAll, mergeLeft } from 'ramda'
import momentTimezone from 'moment-timezone'

import { usePlace } from 'commons/usePlace'
import { dateGeneralFormat } from 'commons/date'
import MessageAlert from 'app/components/MessageAlert'
import { networkErrorDetails, networkErrorToMessage } from 'commons/error'

import { filterType } from 'app/main/plansAndBilling/constants'

export const ActionType = {
  SET_LOADER_USAGE_HISTORY: 'SET_LOADER_USAGE_HISTORY',
  SET_EMPTY_USAGE_HISTORY: 'SET_EMPTY_USAGE_HISTORY',
  SET_USAGE_HISTORY: 'SET_USAGE_HISTORY',
  SET_SELECTED_DATE: 'SET_SELECTED_DATE',
  SET_AI_LOG: 'SET_AI_LOG',
  SET_LOADER_AI_LOG: 'SET_LOADER_AI_LOG',
}

const initialState = {
  params: {
    page: 1,
    limit: 10,
    startDate: moment().subtract(30, 'days').format(dateGeneralFormat),
    endDate: moment().endOf('day').format(dateGeneralFormat),
    timezone: momentTimezone.tz.guess(true),
  },
  dateRang: null,
  isEmptyUsageHistory: false,
  isLoading: true,
  filter: filterType.LAST_THIRTY_DAYS,
  aiCredit: [],
  usageHistory: { data: [], meta: {} },
  isLoadingAi: false,
}

const reducer = (state, action) => {
  switch (action.type) {
    case ActionType.SET_LOADER_USAGE_HISTORY:
      return {
        ...state,
        isLoading: action.value,
      }
    case ActionType.SET_EMPTY_USAGE_HISTORY:
      return {
        ...state,
        isEmptyUsageHistory: true,
        isLoading: false,
      }
    case ActionType.SET_USAGE_HISTORY:
      return {
        ...state,
        usageHistory: action.value,
        params: action.params,
        isLoading: false,
      }
    case ActionType.SET_SELECTED_DATE:
      return {
        ...state,
        filter: action.filter,
        dateRang: action.dateRang,
      }
    case ActionType.SET_LOADER_AI_LOG:
      return {
        ...state,
        isLoadingAi: action.value,
        aiCredit: [],
      }
    case ActionType.SET_AI_LOG:
      return {
        ...state,
        aiCredit: action.value,
        isLoadingAi: false,
      }
    default:
      return state
  }
}

export default function useUsageHistory() {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { notification } = usePlace()
  const { formatMessage } = useIntl()
  const commonDispatch = useDispatch()

  const handleAlertError = useCallback(
    (error) => {
      const { message } = networkErrorToMessage(formatMessage)(error)
      const errorDetails = networkErrorDetails(error)

      return commonDispatch(
        MessageAlert.showError({
          message: message ?? errorDetails.message,
        }),
      )
    },
    [commonDispatch, formatMessage],
  )

  const fetchTeamMembers = useCallback(
    (params = initialState.params, isFirstTime = false) => {
      effects.apis
        .getUsageLogs(params)
        .then((response) => {
          if (
            isFirstTime &&
            response.meta.total === 0 &&
            notification.total === 0
          )
            dispatch({ type: 'SET_EMPTY_USAGE_HISTORY' })
          else
            dispatch({
              type: 'SET_USAGE_HISTORY',
              value: response,
              params: params,
            })
        })
        .catch((error) => handleAlertError(error))
    },
    [handleAlertError],
  )

  const handleFetchUsageHistory = useCallback(
    async (params, isFirstTime = false) => {
      dispatch({ type: 'SET_LOADER_USAGE_HISTORY', value: true })
      await fetchTeamMembers(params, isFirstTime)
    },
    [fetchTeamMembers],
  )

  const handleStartEndDate = (key) => {
    switch (key) {
      case filterType.ALL:
        return { startDate: '', endDate: '' }
      case filterType.TODAY:
        return {
          startDate: moment().startOf('day').format(dateGeneralFormat),
          endDate: moment().endOf('day').format(dateGeneralFormat),
        }
      case filterType.LAST_SEVEN_DAYS:
        return {
          startDate: moment().subtract(7, 'days').format(dateGeneralFormat),
          endDate: moment().endOf('day').format(dateGeneralFormat),
        }
      case filterType.LAST_THIRTY_DAYS:
        return {
          startDate: moment().subtract(30, 'days').format(dateGeneralFormat),
          endDate: moment().endOf('day').format(dateGeneralFormat),
        }
      case filterType.LAST_SIX_MONTHS:
        return {
          startDate: moment().subtract(6, 'months').format(dateGeneralFormat),
          endDate: moment().endOf('day').format(dateGeneralFormat),
        }
      case filterType.LAST_YEAR:
        return {
          startDate: moment().subtract(1, 'years').format(dateGeneralFormat),
          endDate: moment().endOf('day').format(dateGeneralFormat),
        }
      default:
        return { startDate: '', endDate: '' }
    }
  }

  const setFilter = (filter, dateRang = null) => {
    dispatch({
      type: 'SET_SELECTED_DATE',
      filter: filter,
      dateRang: isNil(dateRang) ? null : { ...dateRang, key: 'selection' },
    })
  }

  const handleFilter = (value) => {
    setFilter(value, null)
    handleFetchUsageHistory(
      mergeLeft(handleStartEndDate(value), initialState.params),
    )
  }

  const handleDateFilter = ({ startDate = '', endDate = '' }) => {
    setFilter(null, { startDate, endDate })
    const startEndDate = {
      startDate: moment(startDate).format('YYYY-MM-DD'),
      endDate: moment(endDate).format('YYYY-MM-DD'),
    }

    const setParams = mergeAll([
      { ...state.params, ...startEndDate, page: 1, limit: 10 },
    ])
    handleFetchUsageHistory(setParams)
  }

  const fetchAiPreview = useCallback(
    (requestId) => {
      effects.apis
        .previewAiUsageHistory(requestId)
        .then((response) => {
          dispatch({ type: 'SET_AI_LOG', value: response.data })
        })
        .catch((error) => handleAlertError(error))
    },
    [handleAlertError],
  )

  const handleFetchAiPreview = useCallback(
    async (requestId) => {
      dispatch({ type: 'SET_LOADER_AI_LOG', value: true })
      await fetchAiPreview(requestId)
    },
    [fetchAiPreview],
  )

  return {
    state,
    handleFilter,
    handleDateFilter,
    handleFetchAiPreview,
    handleFetchUsageHistory,
  }
}
